/* eslint-disable complexity,max-statements,max-lines */
import {AxiosPromise} from 'axios';
import FormData from 'form-data';

import {ActionType, EmptyActionType} from '@typescript/common/actionTypes';
import {AddToCartPrescriptionType, QuoteItemType} from '@typescript/cartTypes';
import {CART, QUOTE_ITEM} from 'constants/actions';
import {ENDPOINTS} from 'constants/endpoints';
import {HTTP_REQUEST_HEADER, HTTP_REQUEST_HEADER_CONTENT_TYPE_VALUE} from 'constants/api';
import {instanceGusaBackend} from 'libs/api';

/**
 * Write prescription data into FormData
 *
 * @param {FormData} data Form
 * @param {AddToCartPrescriptionType} prescription Prescription settings
 */
export function appendPrescription(data: FormData, prescription: AddToCartPrescriptionType): void {
    const isTwoPd = (+Object.keys(prescription).some(
        key => key === 'PDstatus' && (prescription[key] === true || prescription[key] === 'true'),
    )).toString();

    Object.keys(prescription).forEach((i: string) => {
        if (i === 'PDstatus') {
            data.append('prescription[is_two_pd]', isTwoPd);
            data.append('prescription[PDstatus]', prescription[i]); // for return and set this variable if user edit glasses
        } else if (
            prescription[i] &&
            ((isTwoPd === '0' && i !== 'pd_left' && i !== 'pd_right') ||
                (isTwoPd === '1' && i !== 'pd')) &&
            i !== 'pd_near'
        ) {
            data.append(`prescription[${i}]`, decodeURIComponent(prescription[i]));
            // @ts-ignore
        } else if (i === 'pd_near' && prescription[i] && prescription[i] !== 'None') {
            // @ts-ignore
            data.append(`prescription[${i}]`, decodeURIComponent(prescription[i]));
        }
    });
}

/**
 * Write options data into FormData
 *
 * @param {FormData} data Form
 * @param {Array<{}>} options Options
 */
export function appendOptions(
    data: FormData,
    options: {option?: string | number | boolean; value?: string | number | boolean}[],
): void {
    options.forEach(option => {
        if (option.option) {
            data.append(`options[${option.option}]`, option.value);
        }
    });
}

/**
 * Write prescription file into FormData
 *
 * @param {FormData} data Form
 * @param {File | string} prescriptionFile Prescription file name
 */
export function appendPrescriptionFile(data: FormData, prescriptionFile: File | string) {
    data.append('prescription_upload', prescriptionFile);
    data.append('prescription_file', '');
    data.append('prescription_file_name', '');
}

/**
 * Write package option into FormData
 *
 * @param {FormData} data Form
 * @param {{}} packageOption Package options (e.g. digital block)
 */
export function appendPackageOption(
    data: FormData,
    packageOption: {package?: string | boolean; option?: string},
) {
    if (packageOption.package) {
        data.append(`package_option[${packageOption.package}]`, packageOption.option);
    }
}

/**
 * Write doctor detail into FormData
 *
 * @param {FormData} data Form
 * @param {{}} drDetail Details
 */
export function appendDoctor(data: FormData, drDetail: {}) {
    Object.keys(drDetail).forEach((i: string) => {
        data.append(`doctor[${i}]`, decodeURIComponent(drDetail[i]));
    });
}

/**
 * Write prism into FormData
 *
 * @param {FormData} data Form
 * @param {string | number} prism Whether prism is added or not
 */
export function appendPrism(data: FormData, prism?: string | number | boolean) {
    data.append('prescription[prism]', prism ? '1' : '0');
}

/**
 * Util for form
 * @param {{}} item - fields form
 * @return {FormData} - data
 */
export function utilFormData(item: {[key: string]: any}): {} {
    const data = new FormData();

    Object.keys(item).forEach((key: string) => {
        if (key === 'prescriptionFile') {
            appendPrescriptionFile(data, item[key]);
        } else if (key === 'send_by') {
            data.append('prescription[send_by]', item[key]);
        } else if (key === 'prism') {
            appendPrism(data, item[key]);
        } else if (key === 'package_option' && item[key]) {
            appendPackageOption(data, item[key]);
        } else if (key === 'option' && item[key]) {
            appendOptions(data, item[key]);
        } else if (key === 'prescription' && item[key]) {
            appendPrescription(data, item[key]);
        } else if (key === 'doctor' && item[key]) {
            appendDoctor(data, item[key]);
        } else if (item[key]) {
            data.append(key, item[key]);
        }
    });

    data.append('ajax', '1');

    return data;
}

/**
 * Add item to cart
 * @param {{}} data - Item configuration for adding to cart
 * @returns {ActionType<AxiosPromise>} Action
 */
export function addToCart(data: {[key: string]: any}): ActionType<AxiosPromise> {
    return {
        type: CART.ADD_TO_CART,
        payload: instanceGusaBackend({
            method: 'post',
            url: ENDPOINTS.ADD_TO_CART_V2,
            data: {...data, productAsHTO: 0},
        }),
        meta: data,
    };
}

/**
 * Get payment amazon
 * @returns {ActionType<AxiosPromise>} Action
 */
export function getPayment(): ActionType<AxiosPromise> {
    return {
        type: CART.GET_PAYMENT_BUTTONS,
        payload: instanceGusaBackend({
            method: 'get',
            headers: {
                [HTTP_REQUEST_HEADER.CONTENT_TYPE]:
                    HTTP_REQUEST_HEADER_CONTENT_TYPE_VALUE.APPLICATION_JSON,
            },
            url: ENDPOINTS.CART_GET_PAYMENT_BUTTONS,
        }),
    };
}

/**
 * Update lenses item to cart
 * It used only for contact lenses configure
 * @param {{}} item Item configuration for updating to cart
 * @returns {ActionType<AxiosPromise>} Action
 */
export function updateCartLenses(item: {[key: string]: any}): ActionType<AxiosPromise> {
    const data = utilFormData(item);

    return {
        type: CART.UPDATE_CART,
        payload: instanceGusaBackend({
            headers: {
                [HTTP_REQUEST_HEADER.CONTENT_TYPE]:
                    HTTP_REQUEST_HEADER_CONTENT_TYPE_VALUE.MULTIPART_FORM_DATA,
            },
            method: 'post',
            url: `${ENDPOINTS.CART_UPDATE_LENSES}/${item.id}`,
            data,
        }),
    };
}

/**
 * Remove cart item by it id
 * @param {string} cartItemId cart item id
 * @param {QuoteItemType} item - All information of removed item
 * @return {ActionType<AxiosPromise, string>} Action
 */
export function removeQuoteItem(
    cartItemId: string,
    item: QuoteItemType,
): ActionType<AxiosPromise, QuoteItemType> {
    return {
        type: CART.REMOVE_QUOTE_ITEM,
        payload: instanceGusaBackend({
            method: 'get',
            url: `${ENDPOINTS.CART_REMOVE_QUOTE_ITEM}/${cartItemId}`,
        }),
        meta: item,
    };
}

/**
 * Initiation of the cart saga
 * @return {EmptyActionType} Action
 */
export function initCartSaga(): EmptyActionType {
    return {
        type: CART.INIT_CART_SAGA,
    };
}

/**
 * Get popup content from special offer banner.
 *
 * @param {string} cmsBlockName - Special offer cms block name
 * @return {ActionType<AxiosPromise>} Action
 */
export const getPopupContent = (cmsBlockName: string): ActionType<AxiosPromise> => ({
    type: CART.GET_POPUP_CONTENT,
    payload: instanceGusaBackend({
        method: 'get',
        headers: {
            [HTTP_REQUEST_HEADER.CONTENT_TYPE]:
                HTTP_REQUEST_HEADER_CONTENT_TYPE_VALUE.MULTIPART_FORM_DATA,
        },
        url: `${ENDPOINTS.CART_GET_BANNER}/?blockCode=${cmsBlockName}`,
    }),
});

/**
 * Get data for saved cart
 * @param {string} quoteId id of saved cart
 * @return {ActionType<AxiosPromise>} Action
 */
export function getQuoteItemsByKey(quoteId: string): ActionType<AxiosPromise> {
    return {
        type: QUOTE_ITEM.GET_ITEMS,
        payload: instanceGusaBackend({
            method: 'get',
            headers: {
                [HTTP_REQUEST_HEADER.CONTENT_TYPE]:
                    HTTP_REQUEST_HEADER_CONTENT_TYPE_VALUE.APPLICATION_JSON,
            },
            url: ENDPOINTS.CART_GET_QUOTE,
            params: {
                cartKey: quoteId,
                version: 'v2.0',
            },
        }),
    };
}

/**
 * Returns the "pending" status for the itemV2 reducer
 *
 * @returns {EmptyActionType} action
 */
export const replaceStatusForItemV2 = (): EmptyActionType => ({type: CART.CLEAR_STATUS_ITEM_V2});
